import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Tabs, 
  Tab, 
  Breadcrumbs, 
  Link,
  Alert,
  CircularProgress,
  Button,
  Snackbar,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
// Import unified components (using available formulation components)
import FormulationSelectorV2 from '../components/formulation/FormulationSelectorV2';
import FormulationCreator from '../components/formulation/FormulationCreator';
import ElementTargetTable from '../components/formulation/ElementTargetTable';
import { Formulation, ElementTarget } from '../types/formulation';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { fetchFormulations, clearSelectedFormulation } from '../store/slices/formulationSlice';
import FeatureGate from '../components/common/FeatureGate';

// Using unified API components (no more V1/V2 confusion)

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`formulation-tabpanel-${index}`}
      aria-labelledby={`formulation-tab-${index}`}
      style={{ padding: '20px 0' }}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const FormulationPage = () => {
  const [tabValue, setTabValue] = useState(0);
  const [editMode, setEditMode] = useState<'edit' | 'duplicate' | null>(null);
  const [notification, setNotification] = useState<{ message: string, severity: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const dispatch = useDispatch();

  // Get the selected formulation from unified Redux state
  const { selectedFormulation, loading, error } = useSelector((state: RootState) => state.formulation);
  
  // Fetch formulations when component mounts
  useEffect(() => {
    dispatch(fetchFormulations() as any);
  }, [dispatch]);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // Clear edit mode when changing tabs
    setEditMode(null);

    // If moving away from "Create New" tab, clear selected formulation
    if (tabValue === 2 && newValue !== 2) {
      dispatch(clearSelectedFormulation());
    }

    setTabValue(newValue);
  };
  
  const handleFormulationSelect = (formulation: Formulation) => {
    // Check if this is a "create new" signal (empty formulation object)
    if (!formulation.id) {
      setTabValue(2); // Switch to Create New tab
    }
  };

  const handleFormulationEdit = (formulation: Formulation) => {
    setEditMode('edit');
    setTabValue(1); // Switch to Element Targets tab
  };

  const handleFormulationDuplicate = (formulation: Formulation) => {
    setEditMode('duplicate');
    setTabValue(1); // Switch to Element Targets tab
  };

  const handleFormulationSave = (updatedFormulation: Formulation) => {
    setEditMode(null);
    setNotification({
      message: `Formulation ${editMode === 'edit' ? 'updated' : 'created'} successfully!`,
      severity: 'success'
    });
    
    // If in create/duplicate mode, switch back to browse tab
    if (tabValue === 2 || editMode === 'duplicate') {
      setTabValue(0);
    }
  };

  const handleNotificationClose = () => {
    setNotification(null);
  };
  // Removed V1/V2 toggle - now using unified API components only

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Formulations
      </Typography>
      
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="formulation breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link color="inherit" href="/">
          Home
        </Link>
        <Typography color="text.primary">Formulations</Typography>
        <Typography color="text.primary">
          {tabValue === 0 ? 'Browse' : tabValue === 1 ? 'Element Targets' : 'Create New'}
        </Typography>
      </Breadcrumbs>

      <Alert severity="info" sx={{ mb: 3 }}>
        Select a formulation to view element targets or create a new custom formulation.
      </Alert>
      
      {/* Using unified API components (no more V1/V2 toggle) */}
      
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            aria-label="formulation tabs"
            sx={{ pl: 2 }}
          >
            <Tab label="Browse Formulations" />
            <Tab label="Element Targets" disabled={!selectedFormulation || loading} />
            <Tab label="Create New" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '7fr 5fr', lg: '8fr 4fr' }, gap: 3 }}>
              <Box>
                <FormulationSelectorV2
                  onSelect={handleFormulationSelect}
                  onEdit={handleFormulationEdit}
                  onDuplicate={handleFormulationDuplicate}
                />
              </Box>
              <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Instructions
                </Typography>
                <Typography variant="body2" paragraph>
                  Select a formulation from the list to view its element targets. You can search for specific formulations or filter by crop type.
                </Typography>
                <Typography variant="body2">
                  To create a new custom formulation, click "Create New Formulation" or select the "Create New" tab above.
                </Typography>
              </Box>
            </Box>
          </Box>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 2 }}>
            {editMode && selectedFormulation ? (
              <FormulationCreator
                initialFormulation={selectedFormulation}
                onSave={handleFormulationSave}
              />
            ) : (
              <>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography variant="h6">
                    Element Targets for {selectedFormulation?.name}
                  </Typography>
                  <Box>
                    <Button 
                      variant="outlined" 
                      color="primary" 
                      onClick={() => setEditMode('edit')}
                      sx={{ mr: 1 }}
                    >
                      Edit Formulation
                    </Button>
                    <Button 
                      variant="outlined"
                      onClick={() => setEditMode('duplicate')}
                    >
                      Duplicate
                    </Button>
                  </Box>
                </Box>
                
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : selectedFormulation && selectedFormulation.elements ? (
                  <ElementTargetTable 
                    targets={selectedFormulation.elements}
                    onChange={(updatedTargets: ElementTarget[]) => {
                      // Just view mode - no editing here
                      console.log('View mode - targets not editable here');
                    }}
                    readOnly={true}
                  />
                ) : (
                  <Typography variant="body2" paragraph>
                    No element targets found for this formulation.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </TabPanel>
          <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 2 }}>
            <FeatureGate action="formulation" showUpgradePrompt={true}>
              <FormulationCreator
                onSave={handleFormulationSave}
              />
            </FeatureGate>
          </Box>
        </TabPanel>
      </Paper>
      
      {/* Notification snackbar for success/error messages */}
      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        message={notification?.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default FormulationPage;
